// Ported from dev-scrips-pm-react src/shared/lib/toast-helpers.ts (PROD-1119 Patients data layer).
import { toast, type ExternalToast } from 'sonner';
import { ApiError } from '@/shared/api/api-client';

/**
 * Generic catch-all message — used whenever we can't extract a more
 * specific server message. Mirrors Flutter's fallback toast text.
 */
const GENERIC_MESSAGE = 'Something went wrong, please contact Scrips support.';

/**
 * ASP.NET Core ValidationProblemDetails shape returned for 400s.
 * Mirrors Flutter `ApiExceptionModel`:
 *   { title?, status?, errors?: Record<string, unknown[]> }
 */
interface ValidationProblemDetails {
  title?: unknown;
  status?: unknown;
  errors?: Record<string, unknown>;
}

/**
 * Surface a server / network failure as a top-right toast.
 *
 * Mirrors Flutter `handleFailure()` (scrips_core/utils/utils.dart:400):
 *   - 400  → flatten the server's field errors and show them, so the
 *            user sees e.g. "Headers are missing: Service Name, …".
 *   - 404  → body text, or "Server not reachable".
 *   - 403 / 409 → a JSON `{ error }` sentence when there is one (PROD-2710).
 *   - else → generic "Something went wrong" toast.
 *
 * The raw error always goes to `console.error` for triage regardless.
 *
 * @param context — short label that goes to console.error for triage
 * @param err     — the actual error / exception to log
 */
export function showServerError(context: string, err: unknown): void {
  // eslint-disable-next-line no-console
  console.error(`[${context}]`, err);
  toast.error(messageFor(err));
}

/** Surface an action-success toast. Message can be specific. */
export function showSuccess(message: string): void {
  toast.success(message);
}

/**
 * Surface a client-side validation warning (e.g. a required field left
 * blank before submitting a filter). Mirrors Flutter's
 * `ToastUtils.showErrorToast` for in-form guards — distinct from
 * `showServerError`, which is for API/network failures.
 */
export function showWarning(message: string, options?: Pick<ExternalToast, 'action' | 'duration'>): void {
  if (options) toast.warning(message, options);
  else toast.warning(message);
}

/** The text a failed request shows the user (server sentence when there is one, else the generic line). */
export function serverErrorMessage(err: unknown): string {
  return messageFor(err);
}

function messageFor(err: unknown): string {
  if (!(err instanceof ApiError)) return GENERIC_MESSAGE;

  if (err.status === 400) {
    const fromBody = parseValidationProblem(err.body);
    if (fromBody) return fromBody;
    // Patients `OrderSets` answers a 400 as `{ error, fields? }` rather than ProblemDetails.
    const fromJson = parseErrorField(err.body);
    if (fromJson) return fromJson;
    return 'Request fields are missing.';
  }

  if (err.status === 403 || err.status === 409) {
    // A refusal or a stale-version conflict with a `{ "error": "..." }` sentence (Patients
    // `OrderSets`, PROD-2710: "Only physicians can manage order sets." / a stale edit). Without a
    // sentence these stay on the generic message, as before.
    return parseErrorField(err.body) ?? GENERIC_MESSAGE;
  }

  if (err.status === 404) {
    // Some services answer a 404 with a JSON `{ "error": "..." }` body (e.g. Patients
    // `POST /Bundle` on a missing or not-owned bundle, PROD-2697). Show the sentence, not
    // the raw JSON.
    const fromJson = parseErrorField(err.body);
    if (fromJson) return fromJson;
    const trimmed = err.body?.trim();
    return trimmed && trimmed.length > 0 && trimmed.length < 200
      ? trimmed
      : 'Server not reachable';
  }

  // 401 is supposed to be handled by the api-client's refresh-on-401
  // retry; if it bubbles up here the refresh failed and we just want
  // the generic message. 402-499 (other), 5xx, and network failures
  // also fall through to generic.
  return GENERIC_MESSAGE;
}

/** `{ "error": "<sentence>" }` → the sentence; anything else → null. Never throws. */
function parseErrorField(rawBody: string | undefined): string | null {
  if (!rawBody) return null;
  try {
    const parsed = JSON.parse(rawBody) as { error?: unknown } | null;
    const msg = typeof parsed?.error === 'string' ? parsed.error.trim() : '';
    return msg.length > 0 && msg.length < 200 ? msg : null;
  } catch {
    return null;
  }
}

/**
 * Parse a 400 response body and return the flattened error string in
 * Flutter's exact format:
 *
 *   errors.values.map(e => e.join(", ")).join("\n")
 *
 * Returns `null` if the body isn't JSON or doesn't have an `errors`
 * object with at least one non-empty array.
 *
 * Exported so tests can exercise sad paths (malformed JSON, missing
 * errors, nested arrays, non-string entries) directly. The global
 * QueryCache / MutationCache `onError` handler depends on this never
 * throwing — anything it can't parse should round-trip to `null` so
 * `messageFor` falls back to a safe default.
 */
export function parseValidationProblem(rawBody: string): string | null {
  if (!rawBody) return null;
  let parsed: ValidationProblemDetails | null = null;
  try {
    parsed = JSON.parse(rawBody) as ValidationProblemDetails;
  } catch {
    return null;
  }
  const errors = parsed?.errors;
  if (!errors || typeof errors !== 'object') return null;

  const lines: string[] = [];
  for (const value of Object.values(errors)) {
    if (Array.isArray(value)) {
      const joined = value.map((v) => String(v).trim()).filter(Boolean).join(', ');
      if (joined) lines.push(joined);
    } else if (typeof value === 'string' && value.trim().length > 0) {
      lines.push(value.trim());
    }
  }

  if (lines.length === 0) {
    // No field-level details — fall back to the top-level title.
    const title = typeof parsed.title === 'string' ? parsed.title.trim() : '';
    return title || null;
  }
  return lines.join('\n');
}
